import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { API_CONFIG, ApiCallingV2Service } from '@zayan-erp/shared';
import {
  DsmAppointment,
  DsmAppointmentCreatePayload,
  DsmAppointmentLocation,
  DsmAppointmentLocationPayload,
  DsmAppointmentQuery,
  DsmAppointmentStatus,
  DsmAppointmentSummary,
  DsmAppointmentUpdatePayload
} from '../models/dsm-appointment.models';

@Injectable({ providedIn: 'root' })
export class DsmAppointmentsService {
  readonly #api = inject(ApiCallingV2Service);
  readonly #baseUrl = `${inject(API_CONFIG).baseUrlV2}/dsm/appointments`;

  readAppointments(query: DsmAppointmentQuery = {}): Observable<DsmAppointment[]> {
    return this.#api
      .getData<DsmAppointment[]>(`${this.#baseUrl}${this.#toQueryString(query)}`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map((appointments: DsmAppointment[] | null) => appointments ?? []));
  }

  readSummary(salesOfficeId: string | null): Observable<DsmAppointmentSummary> {
    return this.#api.getData<DsmAppointmentSummary>(
      `${this.#baseUrl}/summary${this.#toQueryString({ salesOfficeId })}`,
      { showLoader: false, showMessage: false }
    );
  }

  readAppointment(appointmentId: string): Observable<DsmAppointment> {
    return this.#api.getData<DsmAppointment>(`${this.#baseUrl}/${appointmentId}`, {
      showLoader: false,
      showMessage: false
    });
  }

  createAppointment(payload: DsmAppointmentCreatePayload): Observable<DsmAppointment> {
    return this.#api.postData<DsmAppointment>(
      this.#baseUrl,
      { showLoader: false, showMessage: true, message: 'Appointment booked.' },
      payload
    );
  }

  updateAppointment(appointmentId: string, payload: DsmAppointmentUpdatePayload): Observable<DsmAppointment> {
    return this.#api.putData<DsmAppointment>(`${this.#baseUrl}/${appointmentId}`, payload, {
      showLoader: false,
      showMessage: true,
      message: 'Appointment updated.'
    });
  }

  updateStatus(appointmentId: string, status: DsmAppointmentStatus): Observable<DsmAppointment> {
    return this.#api.putData<DsmAppointment>(
      `${this.#baseUrl}/${appointmentId}/status`,
      { status },
      { showLoader: true, showMessage: false }
    );
  }

  startAppointment(appointmentId: string): Observable<DsmAppointment> {
    return this.#api.putData<DsmAppointment>(`${this.#baseUrl}/${appointmentId}/start`, null, {
      showLoader: true,
      showMessage: false
    });
  }

  readLocations(): Observable<DsmAppointmentLocation[]> {
    return this.#api
      .getData<DsmAppointmentLocation[]>(`${this.#baseUrl}/locations`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map((locations: DsmAppointmentLocation[] | null) => locations ?? []));
  }

  createLocation(payload: DsmAppointmentLocationPayload): Observable<DsmAppointmentLocation> {
    return this.#api.postData<DsmAppointmentLocation>(
      `${this.#baseUrl}/locations`,
      { showLoader: false, showMessage: true, message: 'Address added.' },
      payload
    );
  }

  #toQueryString(query: DsmAppointmentQuery): string {
    const params = new URLSearchParams();

    if (query.salesOfficeId) params.set('salesOfficeId', query.salesOfficeId);
    if (query.consultantId) params.set('consultantId', query.consultantId);
    if (query.projectId) params.set('projectId', query.projectId);
    if (query.lotId) params.set('lotId', query.lotId);
    if (query.dealId) params.set('dealId', query.dealId);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    query.statuses?.forEach(status => params.append('statuses', String(status)));
    query.types?.forEach(type => params.append('types', String(type)));

    const text = params.toString();

    return text ? `?${text}` : '';
  }
}
